import { useEffect, useRef, useState } from 'react';
import { useSetAtom } from 'jotai';

import { useAppConfig } from '@common/app-config';
import { useOnceReveal } from '@common/hooks';
import { ZigzagEvent } from '@common/log-manager';
import {
  CLIENT_TO_SERVER_TAB_TYPE_MAP,
  getTabDotKey,
  hideSizeTabTooltipAtom,
  SIZE_TAB_TOOLTIP_KEY,
  TabType,
  useActiveTabTypeAtom,
  useCatalogProductIdParams,
  useFocusTab,
  useGetPdpIntegratedData,
  usePdpBaseInfo,
  usePdpRelatedProductRecommendGroups,
  useProductReviewTotalCount,
  useTimeoutTooltip,
} from '@domains/pdp-common';

import { SOHO_FASHION_CATEGORY } from '../constants/tabs';
import { useProductTabList } from '../hooks/useProductTabList';
import { useSizeInfoTabImpressionLogSend } from '../hooks/useSizeInfoTabImpressionLogSend';
import { useTabsUBL } from '../log/useTabsUBL';

export const useTabsService = () => {
  const param = useCatalogProductIdParams();
  const { isZigzagApp } = useAppConfig();
  const { removeFocusTab, getFocusTab } = useFocusTab();
  const { tabClickUBL } = useTabsUBL();
  const {
    shop,
    product,
    tab_list,
    flags: { is_selected_shop, is_size_tab_visible },
  } = usePdpBaseInfo(param);
  const { pdp_size_info } = useGetPdpIntegratedData({ catalog_product_id: product.id });
  const { review_total_count } = useProductReviewTotalCount(product.id);

  const { has_recommend_items } = usePdpRelatedProductRecommendGroups({
    catalog_product_id: param.catalog_product_id,
    isSelectedShop: Boolean(is_selected_shop),
  });

  const ref = useRef<Element | null>(null);
  const sizeRecommendScrollPosition = useRef(0);

  const [sizeRecommendRef, setSizeRecommendRef] = useState<HTMLDivElement | null>(null);
  const [activeTabType, setActiveTabType] = useActiveTabTypeAtom({
    onUpdate: () => {
      if (!ref.current) return;

      const headerHeight = isZigzagApp ? 0 : 44;
      const dividerHeight = 8;

      const tabsContainerRect = ref.current.getBoundingClientRect();
      const targetScrollY = window.scrollY + tabsContainerRect.top - headerHeight + dividerHeight;

      window.scrollTo({
        top: targetScrollY,
        behavior: 'smooth',
      });
    },
  });

  const isShowSizeTab = is_size_tab_visible || Boolean(pdp_size_info?.item_list.length);
  // 사이즈 비교하기 기능 사용 가능 여부 (is_size_comparable이 true인 경우에만 툴팁 노출)
  const isSizeComparable = pdp_size_info?.item_list[0]?.is_size_comparable ?? false;
  const has_beauty_ingredient_tab = Boolean(product.beauty_info?.beauty_ingredient_detail);

  const is_soho = Boolean(
    SOHO_FASHION_CATEGORY.keys.includes(product.category_key ?? '') &&
      product.managed_category_list?.some((item) => SOHO_FASHION_CATEGORY.ids.includes(item.category_id)),
  );

  const isInfiniteRecommend = Boolean(product.is_brand || is_soho || is_selected_shop);

  const { targetRef, isRevealed } = useOnceReveal();
  useSizeInfoTabImpressionLogSend(isShowSizeTab, isZigzagApp, product.id, isRevealed);

  const tabList = useProductTabList({
    review_total_count,
    has_size_tab: isShowSizeTab,
    has_recommend_tab: has_recommend_items,
    has_beauty_ingredient_tab,
    server_tab_list: tab_list,
    activeTabType,
  });

  const { isTooltipShown: isSizeTabTooltipShown, hideTooltip: hideSizeTabTooltip } = useTimeoutTooltip({
    key: SIZE_TAB_TOOLTIP_KEY,
    time: undefined,
    dep: [isRevealed, isShowSizeTab, isSizeComparable],
    // 사이즈 비교하기 기능 사용 가능한 상품에만 툴팁 노출
    isUnrevealed: () => !isRevealed || !isShowSizeTab || !isSizeComparable,
    isRevealedWithScroll: () => (ref.current?.getBoundingClientRect().top ?? 0) < 50,
    saveOnShow: false,
  });

  // ATF 사이즈 아이콘에서 툴팁을 숨길 수 있도록 콜백 공유
  const setHideSizeTabTooltip = useSetAtom(hideSizeTabTooltipAtom);
  useEffect(() => {
    setHideSizeTabTooltip(() => hideSizeTabTooltip);
  }, [hideSizeTabTooltip, setHideSizeTabTooltip]);

  const combindRef = (node: HTMLDivElement) => {
    if (node) {
      ref.current = node;
      targetRef(node);
    }
  };

  // 탭 활성화(진입) 시 레드닷 숨김 처리 (dot_info가 있는 탭만)
  useEffect(() => {
    const serverTabType = CLIENT_TO_SERVER_TAB_TYPE_MAP[activeTabType];
    if (serverTabType && tab_list?.length) {
      const targetTab = tab_list.find((item) => item.tab.type === serverTabType);
      // dot_info가 있는 탭만 로컬스토리지에 저장
      if (targetTab?.tab.dot_info) {
        try {
          localStorage.setItem(getTabDotKey(serverTabType), 'true');
        } catch {
          // 로컬스토리지 접근 실패 무시
        }
      }
    }
  }, [activeTabType, tab_list]);

  const changeTab = (tabType: TabType) => {
    setActiveTabType(tabType);

    if (tabType === TabType.SIZE_INFO) {
      hideSizeTabTooltip();
    }

    if (tabType !== TabType.REVIEW) {
      tabClickUBL(product.id, tabType);
    }

    if (tabType === TabType.REVIEW) {
      ZigzagEvent.logBraze('browser_product_review', {
        review_count: review_total_count,
        shop_id: shop.id,
        catalog_product_id: product.id,
      });
    }
    if (tabType === TabType.RECOMMEND) {
      ZigzagEvent.logBraze('browser_product_recommend');
    }
  };

  const onSizeRecommendRefChange = (node: HTMLDivElement) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (node) {
      setSizeRecommendRef(node);
      sizeRecommendScrollPosition.current = node.offsetTop - window.innerHeight / 2 + node.clientHeight / 2;
    }
  };

  useEffect(() => {
    const focusTab = getFocusTab();
    if (focusTab) {
      setTimeout(() => {
        const tabType = Number(focusTab) as TabType;
        setActiveTabType(tabType);
        removeFocusTab();

        // ATF 사이즈 진입점을 통한 진입 시 툴팁 숨김 처리
        if (tabType === TabType.SIZE_INFO) {
          hideSizeTabTooltip();
        }
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    sizeRecommendRef,
    isSelectedShop: is_selected_shop,
    isSizeTabTooltipShown,
    isInfiniteRecommend,
    isZigzagApp,
    activeTabType,
    product,
    tabList,
    combindRef,
    changeTab,
    hideSizeTabTooltip,
    onSizeRecommendRefChange,
  };
};
